import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons/faTimesCircle';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Theme } from '@mui/material';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import classNames from "classnames";
import IconButtonOverflowV from 'components/Buttons/IconButtonOverflowH';
import IconComment from 'components/Icons/IconComment';
import IconFollowUser from 'components/Icons/IconFollowUser';
import IconMention from 'components/Icons/IconMention';
import IconQuestionReceived from 'components/Icons/IconQuestionReceived';
import IconQuiz from 'components/Icons/IconQuiz';
import IconReply from 'components/Icons/IconReply';
import IconTrophy from 'components/Icons/IconTrophy';
import IconUserSpecialist from 'components/Icons/IconUserSpecialist';
import MuiAvatar from 'components/MuiExtras/MuiAvatar';
import MuiLink from 'components/MuiExtras/MuiLink';
import MuiColor, { MuiColors } from 'components/MuiExtras/Typography/MuiColor';
import AppResource from 'components/Translations/AppResources';
import AwardTypeName from 'components/Translations/AwardTypeName';
import { Formaters } from 'lib/Formarters';
import HtmlUtils from 'lib/HtmlUtils';
import LibUtils from 'lib/LibUtils';
import { AwardType, FeedType, NotificationType } from 'models/ApiEnums';
import { NotificationDto } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { RoutePaths } from 'routes/RouteConfig';
import { OtherPeopleList } from 'views/Notification/OtherPeople';
import { OtherCoursesList, OtherPublicationsList } from 'views/Notification/OtherPublications';
import { OtherTopicFollowingList } from 'views/Notification/OtherTopicFollowing';
import PublicationIcon from 'views/Publication/Components/PublicationIcon';
import { getPublicationUrl } from 'views/Timeline/Components/FeedHelpers';
import { TimelineActionsRouteState } from 'views/Timeline/TimelineActions';
import { ITimelineCoursesRouteState } from 'views/Timeline/TimelineCourses';

//////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    secondaryRoot: {
        top: '20px'

    }
});


interface IState {
    dialog: any;
    currentTargetId: string;
}

interface IProps {
    containerClassName?: string;
    newItemClassName?: string;
    avatarClassName?: string;
    notification: NotificationDto;
    onCloseMenu: () => void;
    onClick: (e: MouseEvent) => void;
    onItemMenuClick: (e: React.MouseEvent<HTMLElement>, notification: NotificationDto) => void;
}

type Props = IProps & WithTranslation & RouteComponentProps<{}> & WithStyles<typeof styles>;

type ResolveThumbnailResult = {
    url: string;
    title: string;
    icon: JSX.Element;
    color: MuiColors;
};

//////////////////////////////////////////////////////////////////////////////////////

class NotificationMessage extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = { dialog: null, currentTargetId: null };
    }

    private userNameContent(key: any, id: string, name: string) {
        return (
            <MuiLink
                key={key}
                color="secondary"
                onClick={() => HtmlUtils.openUrlInNewWindow(`/user/profile/${id}`, id)}
            >
                {LibUtils.firstWord(name)}
            </MuiLink>
        );
    }

    private resolveLink(n: NotificationDto) {
        //if ((n.groupName == NotificationGroupType.Reaction || n.counter < 2) && n.targetPublicationId && n.targetPublicationType) {
        if (n.targetPublicationId && n.targetPublicationType) {
            var url = getPublicationUrl(n.targetPublicationId, n.targetPublicationType, n.relatedAnswerId || null) + "?fn=1";
            if (url != null && n.relatedCommentId != null) url += `&pcmid=${n.relatedCommentId}`;
            return url;
        }

        return null;
    }

    private resolveTemplate(): string {
        const { notification, t } = this.props;

        const template1: string = t(`sys:NotificationMessages.${NotificationType[notification.notificationType]}_1`);
        const template2: string = t(`sys:NotificationMessages.${NotificationType[notification.notificationType]}_2`);

        const counter = (template1.indexOf("list_user_link") > -1 || template2.indexOf("list_user_link") > -1)
            ? notification.userCounter
            : (template1.indexOf("list_followTopic_link") > -1 || template2.indexOf("list_followTopic_link") > -1)
                ? (notification.notificationData != null && notification.notificationData.hasOwnProperty("TopicList") ? (notification.notificationData as any).TopicList.length : 0)
                : (template1.indexOf("list_course_link") > -1 || template2.indexOf("list_course_link") > -1)
                    ? notification.counter
                    : 0;

        return counter > 1 ? template2 : template1;
    }

    private resolveThumbnail(): ResolveThumbnailResult {

        const { notification } = this.props;

        switch (notification.notificationType) {
            case NotificationType.ReactionReplyComment:
            case NotificationType.ReactionComments:
            case NotificationType.ReactionLikes:
            case NotificationType.ReactionThanks:
                return { url: notification.relatedUserThumbnailUrl, title: notification.relatedUserName, icon: null, color: null };

            case NotificationType.NewCourses:
            case NotificationType.CourseItemDeleted:
            case NotificationType.CourseEnrollClosed:
            case NotificationType.CourseEnrollExpires:
            case NotificationType.CourseItemsAdded:
            case NotificationType.CourseEnrollAdded:
            case NotificationType.CourseEnrollCanceled:
            case NotificationType.SuggestionListNewItem:
            case NotificationType.PublicationApproved:
            case NotificationType.PublicationReproved:
            case NotificationType.PublicationBlocked:
            case NotificationType.PublicationUnblocked:
            case NotificationType.PublicationDenounced:
            case NotificationType.PublicationCommentDenounced:
                return {
                    url: notification.targetPublicationThumbnailUrl,
                    title: notification.targetPublicationTitle,
                    icon: <PublicationIcon publicationType={notification.targetPublicationType} isPublic={true} size="2x" />,
                    color: "secondary"
                };


            case NotificationType.RecommendationPub:
            case NotificationType.RecommendationTopic:
                return {
                    url: notification.relatedUserThumbnailUrl,
                    title: notification.relatedUserName,
                    icon: null,
                    color: "secondary"
                };

            case NotificationType.QuestionRepass:
            case NotificationType.QuestionAnswerReceived:
            case NotificationType.QuestionFollow:
                return {
                    url: notification.targetPublicationThumbnailUrl,
                    title: notification.targetPublicationTitle,
                    icon: notification.notificationType == NotificationType.QuestionRepass
                        ? <IconReply size="2x" />
                        : <IconQuestionReceived size="2x" />,
                    color: "secondary"
                };


            case NotificationType.QuestionAnswerRequested:
                return {
                    url: notification.targetPublicationThumbnailUrl,
                    title: notification.targetPublicationTitle,
                    icon: <FontAwesomeIcon icon={faQuestionCircle} size="2x" />,
                    color: "warning"
                };

            case NotificationType.FollowingTopicMoved:
            case NotificationType.NewKnowledgeTestInTopic:
                return {
                    url: notification.relatedTopicThumbnailUrl,
                    title: notification.relatedTopicName,
                    icon: null,
                    color: null
                };

            case NotificationType.TaskReportPublicatonAbuse:
            case NotificationType.TaskReportCommentAbuse:
            case NotificationType.TaskPublicationForApproval:
                return {
                    url: null, title: null, icon: <FontAwesomeIcon icon={faCheckCircle} size="2x" />, color: "warning"
                };

            case NotificationType.TaskReportCommentAbuseDenied:
            case NotificationType.TaskReportPublicationAbuseDenied:
                return {
                    url: null, title: null, icon: <FontAwesomeIcon icon={faTimesCircle} size="2x" />, color: "error"
                };

            case NotificationType.TaskReportCommentAbuseAccept:
            case NotificationType.TaskReportPublicationAbuseAccept:
                return {
                    url: null, title: null, icon: <FontAwesomeIcon icon={faCheckCircle} size="2x" />, color: "success"
                };

            case NotificationType.MentionUserAdded:
                return {
                    url: null, title: null,
                    icon: <IconMention size="2x" />,
                    color: notification.notificationType == NotificationType.MentionUserAdded ? "success" : "error"
                };


            case NotificationType.TopicEndorsed:
            case NotificationType.NewFollower:
            case NotificationType.FollowingAuthor:
                return {
                    url: notification.relatedUserThumbnailUrl,
                    title: notification.relatedUserName,
                    icon: <IconFollowUser size="2x" />,
                    color: "secondary"
                };

            case NotificationType.FollowingTopic:
                {
                    const topicFollowinData = notification.notificationData["TopicList"];
                    return {
                        url: topicFollowinData[0].ThumbnailUrl,
                        title: topicFollowinData[0].Name,
                        icon: null,
                        color: "secondary"
                    };
                }

            case NotificationType.AwardPublication:
                return {
                    url: null,
                    title: null,
                    icon: <IconTrophy size="2x" />,
                    color: "secondary"
                };

            case NotificationType.Badge:
                return {
                    url: notification.notificationData["ThumbnailUrl"],
                    title: null,
                    icon: null,
                    color: "secondary"
                };

            case NotificationType.AwardQuiz:
                return {
                    url: null,
                    title: null,
                    icon: <IconTrophy size="2x" />,
                    color: "secondary"
                };

            case NotificationType.PendingTestsToExecute:
                return {
                    url: null,
                    title: null,
                    icon: <IconQuiz size="2x" />,
                    color: "secondary"
                };

            case NotificationType.TopicSpecialistByUser:
            case NotificationType.TopicSpecialistByRule:
                return {
                    url: null,
                    title: null,
                    icon: <IconUserSpecialist color="success" active size="2x" />,
                    color: "secondary"
                };
        }

        return null;
    }

    private resolveMessage(): JSX.Element[] {
        const { notification, t } = this.props;
        const r = {
            year: t("clientApp:Glossary.Year"),
            years: t("clientApp:Glossary.Years"),
            month: t("clientApp:Glossary.Month"),
            months: t("clientApp:Glossary.Months"),
            day: t("clientApp:Glossary.Day"),
            days: t("clientApp:Glossary.Days"),
            today: t("clientApp:Glossary.Today"),
            at: t("clientApp:Glossary.At"),
            to: t("clientApp:Glossary.To"),

            point: t("clientApp:Views.Notification.Common.Point"),
            points: t("clientApp:Views.Notification.Common.Points"),
            newItem: t("clientApp:Views.Notification.Common.NewItem"),
            newItems: t("clientApp:Views.Notification.Common.NewItems"),
            newCourse: t("clientApp:Views.Notification.Common.NewCourse"),
            newCourses: t("clientApp:Views.Notification.Common.NewCourses"),

            writtenBy: t("clientApp:Common.Literals.WrittenBy"),
        };

        const template = this.resolveTemplate();
        const tokens: string[] = LibUtils.tokenizerString(template, " .,;?!'\"");
        let firstLine = true;
        let doubleSpace = false;

        let bufferLine: Array<any> = [];

        const message = tokens.reduce((acc: Array<any>, s: string, index: number) => {

            var content: React.ReactNode = null;

            if (s.substr(0, 4) == "{nd_" && s[s.length - 1] == "}" && s.length > 5) {
                const field = s.substr(4, s.length - 5);

                const fieldData =
                    (notification.notificationData == null || typeof notification.notificationData[field] == "undefined")
                        ? ""
                        : notification.notificationData[field];

                if (fieldData) {
                    if (field == "Owner" || field == "Approver") {
                        content = this.userNameContent(index, fieldData.UserId, fieldData.Name);
                    }
                    else if (field == "Points") {
                        content = `${fieldData} ${fieldData == 1 ? r.point : r.points}`;
                    }
                    else if (field == "NewCourseItems") {
                        content = `${fieldData} ${fieldData == 1 ? r.newItem : r.newItems}`;
                    }
                    else if (field == "DaysToExpire") {
                        content = fieldData === 0
                            ? r.today
                            : `${r.at} ${fieldData} ${fieldData == 1 ? r.day : r.days}`;
                    }
                    else if (field == "AwardType") {
                        content = <AwardTypeName
                            key={`notificationMessage_${index}`}
                            awardType={fieldData as AwardType}
                        />;
                    }
                    else if (field == "Message") {
                        content = <span><IconComment />&nbsp;<i>{fieldData}</i></span>;
                    }
                    else {
                        content = fieldData;
                    }
                }
                doubleSpace = false;
            }
            else if (s == "][") {
                acc.push(firstLine
                    ? (<b key={`line${index}`}>{bufferLine}</b>)
                    : (<span key={`line${index}`}>{bufferLine}</span>)
                );
                acc.push(<br key={`br${index}`} />);
                bufferLine = [];
                content = null;
                firstLine = false;
                doubleSpace = false;
            }
            else if (s == " ") {
                if (!doubleSpace)
                    content = s;
                else {
                    content = null;
                    doubleSpace = true;
                }
            }
            else {
                doubleSpace = false;

                switch (s) {
                    case "{list_publication_link}":
                        content = (
                            <>
                                <MuiLink
                                    id={notification.notificationId}
                                    key={`notification_${index}`}
                                    color="secondary"
                                    onClick={
                                        (e: React.MouseEvent<HTMLAnchorElement>) => this.setState({ currentTargetId: e.currentTarget.id })
                                    }
                                >
                                    {
                                        this.state.currentTargetId != notification.notificationId &&
                                        <span>
                                            {
                                                notification.counter <= 1
                                                    ? notification.targetPublicationTitle
                                                    : t("sys:NotificationMessages.publication_and_more", { count: notification.counter - 1, publicationName: notification.targetPublicationTitle })
                                            }
                                        </span>
                                    }
                                </MuiLink>
                                {
                                    this.state.currentTargetId == notification.notificationId &&
                                    <OtherPublicationsList
                                        groupId={notification.groupId}
                                        onClose={() => this.setState({ currentTargetId: null })}
                                    />
                                }
                            </>
                        );
                        break;

                    case "{list_followTopic_link}":
                        {
                            const topicFollowinData = notification.notificationData["TopicList"];
                            content = (
                                <>
                                    <MuiLink
                                        id={notification.notificationId}
                                        key={`notification_${index}`}
                                        color="secondary"
                                        onClick={
                                            (e: React.MouseEvent<HTMLAnchorElement>) => {
                                                if (notification.notificationData["TopicList"].length == 1)
                                                    HtmlUtils.openUrlInNewWindow(RoutePaths.TimelineTopic.replace(":id", topicFollowinData[0].TopicId), topicFollowinData[0].TopicId);
                                                else
                                                    this.setState({ currentTargetId: e.currentTarget.id });
                                            }
                                        }
                                    >
                                        {
                                            this.state.currentTargetId != notification.notificationId &&
                                            <span>
                                                {
                                                    topicFollowinData.length == 1
                                                        ? topicFollowinData[0].Name
                                                        : t("sys:NotificationMessages.topic_following", { count: topicFollowinData.length - 1, topicName: topicFollowinData[0].Name })
                                                }
                                            </span>
                                        }
                                    </MuiLink>
                                    {
                                        this.state.currentTargetId == notification.notificationId &&
                                        <OtherTopicFollowingList
                                            topics={topicFollowinData}
                                            onClose={() => this.setState({ currentTargetId: null })}
                                        />
                                    }
                                </>
                            );
                        }
                        break;

                    case "{list_user_link}":
                        content = (
                            <>
                                <MuiLink
                                    id={notification.notificationId}
                                    key={`notification_${index}`}
                                    color="secondary"
                                    onClick={
                                        (e: React.MouseEvent<HTMLAnchorElement>) => this.setState({ currentTargetId: e.currentTarget.id })
                                    }
                                >
                                    {
                                        this.state.currentTargetId != notification.notificationId &&
                                        <span>
                                            {
                                                notification.userCounter == 1
                                                    ? LibUtils.firstWord(notification.relatedUserName)
                                                    : t("sys:NotificationMessages.user_and_more", { count: notification.userCounter - 1, userName: LibUtils.firstWord(notification.relatedUserName) })
                                            }
                                        </span>
                                    }
                                </MuiLink>
                                {
                                    this.state.currentTargetId == notification.notificationId &&
                                    <OtherPeopleList
                                        groupId={notification.groupId}
                                        onClose={() => this.setState({ currentTargetId: null })}
                                    />
                                }
                            </>
                        );
                        break;

                    case "{user_link}":
                        content = this.userNameContent(index, notification.relatedUserId, notification.relatedUserName);
                        break;

                    case "{user_name}":
                        content = LibUtils.firstWord(notification.relatedUserName);
                        break;

                    case "{old_object_name}":
                        content = notification.relatedOldObjectName;
                        break;

                    case "{taskDenouncements_link}":
                        content = (
                            <MuiLink
                                key={`notification_${index}`}
                                onClick={
                                    (e: React.MouseEvent<HTMLElement>) => {
                                        this.props.onCloseMenu();

                                        this.props.history.push(
                                            RoutePaths.TimelineActions,
                                            {
                                                category: FeedType.TaskDenouncement,
                                            } as TimelineActionsRouteState
                                        );
                                    }
                                }
                            >
                                <AppResource resource="Views.Notification.NotificationMessage.GoToDenouncement" />
                            </MuiLink>
                        );
                        break;

                    case "{taskApprovals_link}":
                        content = (
                            <MuiLink
                                key={`notification_${index}`}
                                onClick={
                                    (e: React.MouseEvent<HTMLElement>) => {
                                        this.props.onCloseMenu();

                                        this.props.history.push(
                                            RoutePaths.TimelineActions,
                                            {
                                                category: FeedType.TaskForYourApproval
                                            } as TimelineActionsRouteState
                                        );
                                    }
                                }
                            >
                                <AppResource resource="Views.Notification.NotificationMessage.GoToApprovals" />
                            </MuiLink>
                        );
                        break;

                    case "{publication_link}":
                        content = (
                            <MuiLink
                                key={`notification_${index}`}
                                target="_blank"
                                href={this.resolveLink(notification)}
                                onClick={() => { this.props.onCloseMenu(); }}
                            >
                                <span>&quot;{notification.targetPublicationTitle}&quot;</span>
                            </MuiLink>
                        );
                        break;

                    case "{publication_name}":
                        content = notification.targetPublicationTitle;
                        break;

                    case "{topic_link}":
                        content = (
                            <MuiLink
                                key={`notification_${index}`}
                                target="_blank"
                                href={RoutePaths.TopicViewer.replace(":id", notification.relatedTopicId)}
                                onClick={() => { this.props.onCloseMenu(); }}
                            >
                                <span>&quot;{notification.relatedTopicName}&quot;</span>
                            </MuiLink>
                        );
                        break;

                    case "{topic_name}":
                        content = notification.relatedTopicName;
                        break;

                    case "{kt_link}":
                        content = (
                            <MuiLink
                                key={`notification_${index}`}
                                onClick={() => {
                                    this.props.history.push(RoutePaths.QuizSelectionHighlight.replace(":id", notification.relatedTopicId));
                                    this.props.onCloseMenu();
                                }}
                            >
                                <span>&quot;{notification.relatedTopicName}&quot;</span>
                            </MuiLink>
                        );
                        break;

                    case "{counter}":
                        content = notification.counter;
                        break;

                    case "{userCounter}":
                        content = notification.userCounter;
                        break;

                    case "{quiz_link}":
                        content = (
                            <MuiLink
                                key={`notification_${index}`}
                                onClick={() => {
                                    this.props.history.push(RoutePaths.QuizSelection);
                                    this.props.onCloseMenu();
                                }}
                            >
                                <span>{t("sys:NotificationMessages.pending_tests_link")}</span>
                            </MuiLink>
                        );
                        break;

                    case "{list_course_link}":
                        content = (
                            <>
                                <MuiLink
                                    id={notification.notificationId}
                                    key={`notification_${index}`}
                                    color="secondary"
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => this.setState({ currentTargetId: e.currentTarget.id })}
                                >
                                    {
                                        this.state.currentTargetId != notification.notificationId &&
                                        <span>
                                            {
                                                notification.counter <= 1
                                                    ? notification.targetPublicationTitle
                                                    : t("sys:NotificationMessages.course_and_more", { count: notification.counter - 1, publicationName: notification.targetPublicationTitle })
                                            }
                                        </span>
                                    }
                                </MuiLink>
                                {
                                    this.state.currentTargetId == notification.notificationId &&
                                    <OtherCoursesList
                                        groupId={notification.groupId}
                                        onClose={
                                            (id) => {
                                                this.setState({ currentTargetId: null }
                                                    , () => {
                                                        if (id) {
                                                            this.props.onCloseMenu();
                                                            this.props.history.push(
                                                                {
                                                                    pathname: RoutePaths.TimelineCourses,
                                                                    state: { id: id } as ITimelineCoursesRouteState,
                                                                    key: Math.random().toString()
                                                                });
                                                        }
                                                    });
                                            }
                                        }
                                    />
                                }
                            </>
                        );
                        break;

                    default:
                        content = s;
                }
            }

            if (content != null) {
                bufferLine.push(content);
            }

            return acc;
        }, []);

        message.push(<span key="message">{bufferLine}</span>);

        return message;
    }

    render() {
        const { notification } = this.props;

        const thumb = this.resolveThumbnail();

        return (
            <>
                {this.state.dialog}
                <ListItem
                    dense={true}
                    disableGutters={true}
                    ContainerProps={{ className: this.props.containerClassName }}
                    alignItems="flex-start"
                    className={
                        classNames(
                            {
                                [this.props.newItemClassName]: notification.containNewNotification
                            }
                        )
                    }
                >
                    {
                        thumb && thumb.url != null
                            ? <ListItemAvatar><MuiAvatar className={this.props.avatarClassName} title={thumb.title} color={thumb.color} src={thumb.url} /></ListItemAvatar>
                            : thumb && thumb.icon != null
                                ? <ListItemAvatar><div className={this.props.avatarClassName}><MuiColor variant={thumb.color}>{thumb.icon}</MuiColor></div></ListItemAvatar>
                                : null
                    }

                    <ListItemText
                        primary={this.resolveMessage()}
                        secondary={Formaters.relativeTime(notification.creationDate)}
                    />

                    <ListItemSecondaryAction className={this.props.classes.secondaryRoot} >
                        <IconButtonOverflowV
                            edge="end"
                            size="small"
                            onClick={(e) => this.props.onItemMenuClick(e, notification)}
                        />
                    </ListItemSecondaryAction>
                </ListItem>
            </>
        );
    }
}

export default withRouter(withTranslation(["clientApp", "sys"])(withStyles(styles)(NotificationMessage)));